import Phaser from "phaser";
import { FRAMES, durationMs, resolveColour, type EntityState } from "odyssey-shared";
import { UI_ATLAS_KEY } from "../assets.ts";
import type { WorldUiModule } from "../hook.ts";
import { inVisionCap, onScreen } from "../nameplates/layout.ts";
import type { NameplateChoices } from "../../settings.ts";
import { PLATE_LIFT, barPenaltyOf, barShown } from "../health-bars/bar.ts";
import { PLAIN_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { BUBBLE_LINE_GAP, bubbleLayout, bubbleLines, bubbleSize, newBubbleLayout, type BubbleLayout } from "./layout.ts";
import { BubblePool } from "./pool.ts";
import type { BubbleReading, BubbleReport, SpokenLine } from "./report.ts";

/**
 * Chat bubbles on the canvas (P1.9-T23; ADR-068 decision 9 and its amendment of 2026-09-27; ADR-061 decisions 1 to
 * 3 and its amendments): a Local line shows over its speaker's nameplate for the motion table's `bubble-min` on the
 * scene's clock, in a `NineSlice` of the atlas's `ui/frame/bubble` holding one `BitmapText` in the plain pixel
 * font at 1x, with the atlas's `ui/chat/bubble-tail_5` hanging under it towards the speaker, drawn by the UI
 * camera, so a bubble pixel is a HUD pixel and the world zoom moves a bubble, never sizes it. One bubble a speaker:
 * a new line replaces the text of the last and restarts its time.
 *
 * The page says every `said` line into the report's queue (`report.ts`), and each frame the surface takes the
 * waiting lines, then visits the characters the scene draws, then returns the bubbles no frame kept (`pool.ts`). A
 * bubble shows only while its speaker is drawn, in the viewer's sight or targeted (ADR-061's amendment for P1.9-T73),
 * inside the vision cap around the local character, on the canvas,
 * and holding in the view the two names the line carried, since a handle can pass to another character; a speaker
 * outside vision is in the chat log alone. Bubbles of speakers standing close overlap, as nameplates do, and every
 * bubble draws over every nameplate.
 *
 * The ink is the text-on-light colour, dark on the vellum fill, from the plain font, which has no outline: the
 * outlined font's black ring would merge with dark ink into one mark across a word. The face's advances keep a clear
 * column after every glyph's ink, so the letters stand a pixel apart.
 *
 * A frame of steady play allocates nothing: the lines are taken and the characters visited through functions made
 * once, the projection and the layout write into objects made once, and a bubble's text is set and its frame sized
 * only when a line changes them, and the frame, the tail and the text move only when their anchor or the UI scale
 * does.
 */

/**
 * The bubble frame in the UI atlas, painted from the token ring stack.
 */
const BUBBLE_FRAME = "ui/frame/bubble";

/**
 * How long a line shows, in ms of the scene's clock: the motion table's `bubble-min`, 100 frames.
 */
const SHOW_MS = durationMs("bubble-min");

/**
 * The bubble's tail in the UI atlas, painted from its pixel grid.
 */
const TAIL_FRAME = "ui/chat/bubble-tail_5";

/**
 * The ink as a tint: the text-on-light colour, the design's dark bubble ink on the vellum fill.
 */
const INK = Number.parseInt(resolveColour("text-on-light").slice(1), 16);

/**
 * The depth of every bubble object in the UI layer, above the nameplates' objects at 0. The layer sorts its
 * objects by depth and keeps the order they were added among equals, and both surfaces make their objects as
 * characters and lines arrive, so the order of making cannot keep a bubble over a plate made after it.
 */
export const BUBBLE_DEPTH = 1;

/**
 * The largest handle a snapshot carries.
 */
const MAX_HANDLE = 0xffff;

/**
 * One bubble: its frame, its tail and its text, the line it shows (the speaker's two names, the text drawn, its lines and the
 * frame's size) and until when, and where it was last placed, so a frame that changes nothing touches nothing.
 */
interface Bubble {
  readonly frame: Phaser.GameObjects.NineSlice;
  readonly tail: Phaser.GameObjects.Image;
  readonly text: Phaser.GameObjects.BitmapText;
  until: number;
  firstName: string;
  familyName: string;
  drawn: string;
  lines: number;
  width: number;
  height: number;
  left: number;
  top: number;
  scale: number;
  shown: boolean;
}

/**
 * Hides a bubble's objects, if shown.
 *
 * @param bubble The bubble.
 */
function hide(bubble: Bubble): void {
  if (bubble.shown) {
    bubble.frame.setVisible(false);
    bubble.tail.setVisible(false);
    bubble.text.setVisible(false);
    bubble.shown = false;
  }
}

/**
 * Whether a line's handle is one a snapshot carries, so the pool can hold its bubble.
 *
 * @param handle The handle the line names.
 * @returns True for a whole number from 0 to 65535.
 */
function isHandle(handle: number): boolean {
  return Number.isInteger(handle) && handle >= 0 && handle <= MAX_HANDLE;
}

/**
 * The chat bubbles surface.
 *
 * @param report The game report's bubbles entry: its switch, the queue of lines said, and where the pool's counts
 *   and readings go.
 * @param choices The viewer's Nameplates choices as they stand, which decide whether a health bar lifts the bubble.
 * @returns The surface's module, listed after the nameplates.
 */
export function bubbles(report: BubbleReport, choices: Readonly<NameplateChoices>): WorldUiModule {
  return {
    id: "bubbles",
    /**
     * Builds the pool, the frame's taker and visitor, and lists the bubbles for the report. Nothing is drawn or made
     * until the first line is said.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      const insets = FRAMES.bubble.insets;
      const pool = new BubblePool<Bubble>(() => {
        const frame = context.add(new Phaser.GameObjects.NineSlice(scene, 0, 0, UI_ATLAS_KEY, BUBBLE_FRAME, insets.left + insets.right, insets.top + insets.bottom));
        frame.setOrigin(0, 0).setDepth(BUBBLE_DEPTH).setVisible(false);
        const tail = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, TAIL_FRAME));
        tail.setOrigin(0, 0).setDepth(BUBBLE_DEPTH).setVisible(false);
        const text = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PLAIN_FONT_KEY, "", pixelFontSize("nameplate")));
        text.setOrigin(0, 0).setLineSpacing(BUBBLE_LINE_GAP).setTint(INK).setDepth(BUBBLE_DEPTH).setVisible(false);
        return { frame, tail, text, until: 0, firstName: "", familyName: "", drawn: "", lines: 0, width: 0, height: 0, left: Number.NaN, top: Number.NaN, scale: 0, shown: false };
      });
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const layout: BubbleLayout = newBubbleLayout();
      const camera = context.worldCamera;
      let frameNumber = 0;
      let now = 0;
      let shown = 0;
      let ownHandle = -1;

      /**
       * Starts or replaces a speaker's bubble for a line taken from the queue, running for `SHOW_MS` from the
       * frame's time. A line taken while the switch is off, naming a handle no snapshot carries, or drawing nothing
       * but spaces starts nothing. The text is set and the frame sized only when the text drawn changes.
       *
       * @param line The line.
       */
      const start = (line: SpokenLine): void => {
        if (!report.enabled || !isHandle(line.handle)) {
          return;
        }
        const lines = bubbleLines(line.text);
        if (lines.length === 0) {
          return;
        }
        const bubble = pool.start(line.handle, frameNumber, now + SHOW_MS);
        bubble.firstName = line.firstName;
        bubble.familyName = line.familyName;
        const drawn = lines.join("\n");
        if (drawn !== bubble.drawn) {
          const size = bubbleSize(lines);
          bubble.text.setText(drawn);
          bubble.frame.setSize(size.width, size.height);
          bubble.drawn = drawn;
          bubble.lines = lines.length;
          bubble.width = size.width;
          bubble.height = size.height;
          bubble.left = Number.NaN;
        }
      };

      /**
       * Brings one drawn character's bubble, if it holds one whose time runs, up to date for the frame: shown over
       * its nameplate when the view holds the line's two names for it, it stands inside the vision cap and the
       * bubble lies on the canvas, hidden otherwise.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param state Its state, with its name and its Health percentage, whose bar lifts the plate and the bubble over it.
       * @param head How far its art reaches above its feet, in world pixels, which the bubble stands over.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73): concealed from the viewer and not
       *   targeted; its bubble is kept, hidden, its time still running, so a line said out of sight shows when the
       *   speaker steps into view within its time, and the chat log has it either way.
       */
      const visit = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean): void => {
        const bubble = pool.keep(handle, frameNumber, now);
        if (bubble === undefined) {
          return;
        }
        if (hidden || state.name.first !== bubble.firstName || state.name.family !== bubble.familyName || !inVisionCap(x - local.x, y - local.y)) {
          hide(bubble);
          return;
        }
        const scale = context.scale.current;
        context.project(x, y - head, anchor);
        bubbleLayout(Math.round(anchor.x * scale), Math.round(anchor.y * scale) - (barShown(state.health, handle === ownHandle, choices, barPenaltyOf(state.buffs)) ? PLATE_LIFT * scale : 0), scale, bubble.width, bubble.height, layout);
        if (!onScreen(layout.deviceLeft, layout.deviceTop, layout.deviceWidth, layout.deviceHeight, camera.width, camera.height)) {
          hide(bubble);
          return;
        }
        if (layout.deviceLeft !== bubble.left || layout.deviceTop !== bubble.top || scale !== bubble.scale) {
          bubble.frame.setPosition(layout.frameX, layout.frameY);
          bubble.tail.setPosition(layout.tailX, layout.tailY);
          bubble.text.setPosition(layout.textX, layout.textY);
          bubble.left = layout.deviceLeft;
          bubble.top = layout.deviceTop;
          bubble.scale = scale;
        }
        if (!bubble.shown) {
          bubble.frame.setVisible(true);
          bubble.tail.setVisible(true);
          bubble.text.setVisible(true);
          bubble.shown = true;
        }
        shown += 1;
      };

      /**
       * Returns a bubble no frame kept: hidden, and placed afresh by the next speaker that takes it. Its text stays,
       * so a line drawing the same text sets nothing.
       *
       * @param bubble The bubble.
       */
      const release = (bubble: Bubble): void => {
        hide(bubble);
        bubble.left = Number.NaN;
      };

      /**
       * Reads every bubble held, for the report: made on request, never on a frame.
       *
       * @returns One reading a bubble.
       */
      report.list = (): readonly BubbleReading[] => {
        const readings: BubbleReading[] = [];
        pool.forEachActive((bubble, handle) => {
          const chars = bubble.text.fontData.chars;
          readings.push({
            handle,
            text: bubble.text.text,
            lines: bubble.lines,
            undrawn: [...bubble.text.text].filter((character) => character !== "\n" && chars[character.charCodeAt(0)] === undefined).length,
            shown: bubble.shown,
            left: bubble.frame.x,
            top: bubble.frame.y,
            width: bubble.width,
            height: bubble.height,
            scale: bubble.scale,
            tail: { left: bubble.tail.x, top: bubble.tail.y, width: bubble.tail.width, height: bubble.tail.height, shown: bubble.tail.visible },
            inUiLayer: bubble.frame.displayList === context.layer && bubble.tail.displayList === context.layer && bubble.text.displayList === context.layer,
          });
        });
        return readings;
      };

      /**
       * Where a speaker's bubble drawn on the last frame stands, for the emoji surface listed after this one.
       *
       * @param handle The speaker's handle.
       * @returns The frame's top in device pixels, or NaN when no bubble of the speaker's is drawn.
       */
      report.topOf = (handle: number): number => {
        const bubble = pool.find(handle);
        return bubble !== undefined && bubble.shown ? bubble.top : Number.NaN;
      };

      return {
        /**
         * Takes the lines said since the last frame, visits every character drawn this frame, then returns the
         * bubbles no visit kept. With the switch off, or no local character drawn, nothing is visited and every
         * bubble goes back.
         *
         * @param time The scene's time in ms, which a line's showing is counted on.
         */
        update(time) {
          frameNumber += 1;
          now = time;
          shown = 0;
          report.take(start);
          if (report.enabled && context.characters.local(local)) {
            ownHandle = context.characters.localHandle();
            context.characters.forEach(visit);
          }
          pool.sweep(frameNumber, release);
          report.active = pool.active;
          report.free = pool.free;
          report.created = pool.created;
          report.shown = shown;
        },
        /**
         * Forgets the report's list and places, which would read the destroyed bubbles.
         */
        destroy() {
          /**
           * Lists no bubble, since none is held once the scene has shut down.
           *
           * @returns No readings.
           */
          report.list = (): readonly BubbleReading[] => [];
          /**
           * Places no bubble, since none is held once the scene has shut down.
           *
           * @returns NaN.
           */
          report.topOf = (): number => Number.NaN;
        },
      };
    },
  };
}
